import { createColumnHelper, CellContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ModelRecord } from 'spraypaint/lib-esm/model';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserManagerRole } from '@/modules/authentification/hooks';
import {
  DateFilter,
  getDateFilterFn,
} from '@/modules/common/components/table/filter/DateFilter';
import classNames from 'classnames';
import { AmountWithCurrency } from '@/modules/common/components/fragments/AmountText';
import SepaTransaction from '../../../../../app/spraypaint/sepaTransactions/SepaTransaction';
import {
  ButtonOnClickHandler,
  ButtonPrimary,
} from '../../../../common/components/buttons';
import { useBankReconciliationTransition } from '../../hooks/useBankReconciliationTransition';
import { CompletedValidationModal } from '../../bankAssociation/components/CompletedValidationModal';
import {
  getSupInfFilter,
  SupInfFilter,
} from '../../../../common/components/table/filter/SupInfFilter';
import { TableHeaderLabelTooltiped } from '../../../../common/components/tooltip/TableHeaderAmountTooltip';

const columnHelper = createColumnHelper<ModelRecord<SepaTransaction>>();

export const commonColumns = () => {
  const { t } = useTranslation();

  return [
    columnHelper.accessor('legalEntity.name', {
      header: t('front.sepa.show.table.legalEntity') as string,
      cell: (info) => (
        <div className="text-center">
          <p>{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('paymentDate', {
      header: t('front.sepa.show.table.paymentDate') as string,
      filterFn: getDateFilterFn(),
      meta: {
        filter: {
          Component: DateFilter,
        },
      },
      cell: (info) => (
        <div className="text-center">
          <p>{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('label', {
      header: t('front.sepa.show.table.label') as string,
      cell: (cell) => {
        const { original } = cell.row;
        return (
          <div className="text-left">
            <p>
              {original.label ||
                original.endToEndId ||
                original.additionalInformation}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor('debitorName', {
      // filter working
      header: t('front.sepa.show.table.debitorName') as string,
      cell: (info) => (
        <div className="text-left">
          <p>{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('customAmount', {
      header: () => (
        <TableHeaderLabelTooltiped
          label={t('front.sepa.show.table.amount')}
          msg={t('front.table.header.amount.tooltipMsg')}
        />
      ),
      filterFn: getSupInfFilter(),
      meta: {
        filter: {
          Component: SupInfFilter,
        },
      },
      cell: (cell) => {
        const { direction, amount } = cell.row.original;
        const amountClassNames = classNames(
          'flex pl-7 pr-3 h-[100%] items-center justify-end',
          { 'text-color-txt-failure': direction === 'debit' },
        );
        return (
          <div className={amountClassNames}>
            <AmountWithCurrency
              amount={
                direction === 'credit'
                  ? amount?.amount || 0
                  : -(amount?.amount || 0)
              }
              currencyIsoCode={amount?.currency || 'EUR'}
            />
          </div>
        );
      },
    }),
  ];
};

export const awaitingBankTransferColumns = (id: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isOnlyManager = useUserManagerRole();

  const ActionsCell = ({
    cell,
  }: CellContext<ModelRecord<SepaTransaction>, unknown>) => {
    const data = cell.row.original;

    const handleOnSuccess = () => {
      queryClient.invalidateQueries(['sepa-files', id]);
    };
    const { mutate, isLoading } =
      useBankReconciliationTransition(handleOnSuccess);

    const [comment, setComment] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOnClick: ButtonOnClickHandler = (event) => {
      event.stopPropagation();
      event.preventDefault();
      mutate({
        resourceId: data.id,
        transitionName: 'orphan',
        transitionParams: { comment },
      });
    };

    const handleFill = (e: React.ChangeEvent<HTMLInputElement>) => {
      setComment(e.target.value);
    };

    const handleSetIsModalOpen: ButtonOnClickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsModalOpen((prev) => !prev);
    };

    return (
      <div className="flex justify-center items-center h-[100%]">
        <CompletedValidationModal
          handleSetCloseModal={handleSetIsModalOpen}
          isOpen={isModalOpen}
          handleOnClick={handleOnClick}
          isLoading={isLoading}
          handleFill={handleFill}
        />
        <ButtonPrimary
          isLoading={isLoading}
          onClick={handleSetIsModalOpen}
          disabled={isOnlyManager}
        >
          {t('default.button.renounce')}
        </ButtonPrimary>
      </div>
    );
  };

  return [
    ...commonColumns(),
    columnHelper.accessor('matchingCandidatesCount', {
      header: t('front.sepa.show.table.matchingCandidates') as string,
      filterFn: getSupInfFilter(),
      meta: {
        filter: {
          Component: SupInfFilter,
        },
      },
    }),
    columnHelper.accessor('actions', {
      header: t('default.actions'),
      enableColumnFilter: false,
      enableSorting: false,
      cell: ActionsCell,
    }),
  ];
};

export const validatedBankTransferColumns = (id: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isOnlyManager = useUserManagerRole();

  const ActionsCell = ({
    cell,
  }: CellContext<ModelRecord<SepaTransaction>, unknown>) => {
    const data = cell.row.original;
    const handleOnSuccess = () => {
      queryClient.invalidateQueries(['sepa-files', id]);
    };
    const { mutate } = useBankReconciliationTransition(handleOnSuccess);

    const handleOnClick: ButtonOnClickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      mutate({
        resourceId: data.id,
        transitionName:
          data.status === 'orphaned' ? 'back_to_pending_validation' : 'cancel',
      });
    };

    return (
      <div className="flex justify-center items-center h-[100%]">
        {data.status !== 'duplicate' && (
          <ButtonPrimary onClick={handleOnClick} disabled={isOnlyManager}>
            {t('default.button.cancel')}
          </ButtonPrimary>
        )}
      </div>
    );
  };

  return [
    ...commonColumns(),
    columnHelper.accessor('status', {
      header: t('default.status'),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('actions', {
      header: t('default.actions'),
      enableColumnFilter: false,
      enableSorting: false,
      cell: ActionsCell,
    }),
  ];
};
